import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Search, AlertTriangle, AlertCircle, Info, RefreshCw,
  Globe, FileText, Link2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { sitesAPI, seoAPI } from '@/lib/api';
import { toast } from 'sonner';

export default function SEODashboardPage() {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [audit, setAudit] = useState(null);
  const [linkGraph, setLinkGraph] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    sitesAPI.list({}).then(res => {
      setSites(res.data);
      if (res.data.length > 0) setSelectedSite(res.data[0].id);
    }).catch(() => {});
  }, []);

  const loadAudit = useCallback(async () => {
    if (!selectedSite) return;
    setLoading(true);
    try {
      const [auditRes, graphRes] = await Promise.all([
        seoAPI.audit(selectedSite),
        seoAPI.linkGraph(selectedSite),
      ]);
      setAudit(auditRes.data);
      setLinkGraph(graphRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedSite]);

  useEffect(() => {
    loadAudit();
  }, [loadAudit]);

  const critical = audit?.items?.filter(i => i.severity === 'critical') || [];
  const warnings = audit?.items?.filter(i => i.severity === 'warning') || [];
  const infos = audit?.items?.filter(i => i.severity === 'info') || [];

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight font-['Space_Grotesk']">SEO Paneli</h1>
          <p className="text-sm text-muted-foreground mt-1">Site SEO sagligini denetleyin</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedSite} onValueChange={setSelectedSite}>
            <SelectTrigger className="w-[240px] bg-secondary/40 border-border/70">
              <SelectValue placeholder="Site secin" />
            </SelectTrigger>
            <SelectContent>
              {sites.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.brand_name} ({s.domain})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={loadAudit} variant="outline" size="icon" className="border-border/70">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </motion.div>

      {/* KPI */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-border/70">
          <CardContent className="pt-5">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Toplam Sayfa</p>
            <p className="text-2xl font-semibold mt-1">{audit?.total_pages || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-border/70">
          <CardContent className="pt-5">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Kritik Sorun</p>
            <p className="text-2xl font-semibold mt-1 text-red-400">{critical.length}</p>
          </CardContent>
        </Card>
        <Card className="border-border/70">
          <CardContent className="pt-5">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Uyari</p>
            <p className="text-2xl font-semibold mt-1 text-amber-400">{warnings.length}</p>
          </CardContent>
        </Card>
        <Card className="border-border/70">
          <CardContent className="pt-5">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Ic Linkler</p>
            <p className="text-2xl font-semibold mt-1">{audit?.internal_links_count || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Audit Items */}
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 rounded-xl shimmer" />)}</div>
      ) : (
        <div className="space-y-4">
          {critical.length > 0 && (
            <Card className="border-border/70">
              <CardHeader><CardTitle className="text-base flex items-center gap-2 text-red-400"><AlertCircle className="w-4 h-4" /> Kritik Sorunlar ({critical.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {critical.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 rounded-lg border-l-2 border-l-red-400 bg-red-400/5">
                    <div>
                      <div className="flex items-center gap-2"><span className="font-mono text-xs">{item.page_slug}</span></div>
                      <p className="text-sm mt-1">{item.message}</p>
                      {item.suggestion && <p className="text-xs text-muted-foreground mt-1">{item.suggestion}</p>}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {warnings.length > 0 && (
            <Card className="border-border/70">
              <CardHeader><CardTitle className="text-base flex items-center gap-2 text-amber-400"><AlertTriangle className="w-4 h-4" /> Uyarilar ({warnings.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {warnings.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 rounded-lg border-l-2 border-l-amber-400 bg-amber-400/5">
                    <div>
                      <span className="font-mono text-xs">{item.page_slug}</span>
                      <p className="text-sm mt-1">{item.message}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {infos.length > 0 && (
            <Card className="border-border/70">
              <CardHeader><CardTitle className="text-base flex items-center gap-2 text-blue-400"><Info className="w-4 h-4" /> Bilgi ({infos.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {infos.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 rounded-lg border-l-2 border-l-blue-400 bg-blue-400/5">
                    <div>
                      <span className="font-mono text-xs">{item.page_slug}</span>
                      <p className="text-sm mt-1">{item.message}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Link Graph */}
      {linkGraph && linkGraph.nodes?.length > 0 && (
        <Card className="border-border/70">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Link2 className="w-4 h-4" /> Ic Link Grafigi</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {linkGraph.nodes.map(node => {
                const inbound = linkGraph.edges.filter(e => e.target === node.id).length;
                const outbound = linkGraph.edges.filter(e => e.source === node.id).length;
                const isOrphan = linkGraph.orphans?.includes(node.id);
                return (
                  <div key={node.id} className={`p-3 rounded-lg border ${isOrphan ? 'border-amber-400/30 bg-amber-400/5' : 'border-border/70'}`}>
                    <p className="font-mono text-xs text-[hsl(var(--primary))] truncate">{node.id}</p>
                    <div className="flex gap-3 mt-1.5 text-[10px] text-muted-foreground">
                      <span>Gelen: {inbound}</span>
                      <span>Giden: {outbound}</span>
                    </div>
                    {isOrphan && <p className="text-[10px] text-amber-400 mt-1">Yetim sayfa</p>}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
